import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProgressBarService } from '@services/progress-bar.service';
import { MifSubscriptionAuthService } from '@shared/auth/mif-subscription.auth.service';
import { ResumeAuthService } from '@shared/auth/resume-auth.service';
import { getCurrentProductName } from '@shared/extensions/extensions';
import { AuthorizationDeliveryMode } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import { parseRequestCodeClientError } from '../../../../store/client-store/client.helpers';
import { ClientAuthErrorCode, ClientAuthorizationStatus } from '../../../../store/client-store/client.models';
import { AUTH_TOO_MANY_REQUESTS_MESSAGE } from '../../../constants/auth-messages';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-resume-check',
  templateUrl: './resume-check.component.html',
  styleUrls: ['./resume-check.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResumeCheckComponent implements OnInit {
  userId: string | null = null;
  sessionId: string | null = null;
  returnUrl: string | null = null;
  otpRequestFailed = false;
  showTooManyRequestsScreen = false;
  showOtpCheck = false;
  productName = getCurrentProductName();
  readonly tooManyRequestsMessage = AUTH_TOO_MANY_REQUESTS_MESSAGE;
  private isNavigatingAfterAuthorization = false;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly progressBarService: ProgressBarService,
    private readonly mifSubscriptionAuthService: MifSubscriptionAuthService,
    private readonly clientFacade: ClientFacade,
    private readonly customerService: CustomerService,
    private readonly resumeAuthService: ResumeAuthService
  ) {}

  ngOnInit(): void {
    this.progressBarService.sendStep(0);
    this.clientFacade.cancelClientAuthentication();
    this.initClientAuthorizationSubscription();
    this.userId = this.route.snapshot.queryParamMap.get('userId');
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (!this.userId) {
      this.otpRequestFailed = true;
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.customerService.userId = this.userId;

    this.mifSubscriptionAuthService
      .resumeSessionByUserId(this.userId, AuthorizationDeliveryMode.Sms)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: authResponse => {
          this.sessionId = authResponse.sessionId || null;
          this.showOtpCheck = !!this.sessionId;
          this.showTooManyRequestsScreen = false;
          this.otpRequestFailed = !this.showOtpCheck;
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          const authError = parseRequestCodeClientError(error);

          this.showOtpCheck = false;
          this.showTooManyRequestsScreen = authError.errorCode === ClientAuthErrorCode.TooManyRequests;
          this.otpRequestFailed = !this.showTooManyRequestsScreen;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  private initClientAuthorizationSubscription(): void {
    this.clientFacade.clientAuthorizationStatus$
      .pipe(untilDestroyed(this))
      .subscribe(async clientAuthorizationStatus => {
        if (
          clientAuthorizationStatus !== ClientAuthorizationStatus.Authorized ||
          this.isNavigatingAfterAuthorization ||
          !this.userId
        ) {
          return;
        }

        await this.processSuccessfulOtp();
      });
  }

  private async processSuccessfulOtp(): Promise<void> {
    if (!this.userId) {
      return;
    }

    this.isNavigatingAfterAuthorization = true;

    try {
      await this.resumeAuthService.navigateAfterAuthorization(this.userId, this.returnUrl);
    } catch (error) {
      await this.customerService.clearAllData();

      if (this.resumeAuthService.isUnauthorizedError(error)) {
        await this.resumeAuthService.redirectToResume(this.userId, this.returnUrl);
        return;
      }

      await this.router.navigate(['subscription-time-end']);
    } finally {
      this.isNavigatingAfterAuthorization = false;
    }
  }
}
